/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import keyBy from 'lodash/keyBy';
// import omit from 'lodash/omit';

import * as types from '../constants/ActionTypes';

import { defaultFetchState } from 'reducerUtils';

// Reducers for the service configurations
export const factoryTalkServiceConfigurationsReducer = (state = defaultFetchState, action) => {
  switch (action.type) {
    case types.GET_FACTORYTALK_SERVICE_CONFIGURATIONS_REQUEST:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
      };
    case types.GET_FACTORYTALK_SERVICE_CONFIGURATIONS_FAILURE:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case types.GET_FACTORYTALK_SERVICE_CONFIGURATIONS_SUCCESS:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: keyBy(action.payload, 'serviceName'),
      };
    case types.DELETE_FACTORYTALK_SERVICE_CONFIGURATION_REQUEST:
    case types.UPDATE_FACTORYTALK_SERVICE_CONFIGURATION_REQUEST:
    case types.CREATE_FACTORYTALK_SERVICE_CONFIGURATION_REQUEST:
      return {
        ...state,
        updatingData: true,
        updateDataError: null,
      };
    case types.DELETE_FACTORYTALK_SERVICE_CONFIGURATION_FAILURE:
    case types.CREATE_FACTORYTALK_SERVICE_CONFIGURATION_FAILURE:
    case types.UPDATE_FACTORYTALK_SERVICE_CONFIGURATION_FAILURE:
      return {
        ...state,
        updatingData: false,
        updateDataError: action.payload,
      };
    case types.DELETE_FACTORYTALK_SERVICE_CONFIGURATION_SUCCESS:
    case types.CREATE_FACTORYTALK_SERVICE_CONFIGURATION_SUCCESS:
    case types.UPDATE_FACTORYTALK_SERVICE_CONFIGURATION_SUCCESS:
      return {
        ...state,
        // Not gonna update the data on the delete since we always reload on return
        updatingData: false,
        updateDataError: null,
      };

    default:
      return state;
  }
};

// Selectors for service configuration(s)
export const getFactoryTalkServiceConfigurations = state =>
  Object.values(state.factoryTalkServiceConfigurations?.data || {}) || [];

export const factoryTalkServiceConfigurationsIsFetchingData = state =>
  state.factoryTalkServiceConfigurations?.fetchingData;

export const isFactoryTalkServiceConfigurationUpdating = state =>
  state.factoryTalkServiceConfigurations?.updatingData;
export const getFactoryTalkServiceConfigurationUpdateError = state =>
  state.factoryTalkServiceConfigurations?.updateDataError;

/**
 * @param name {string}, the service name to retrieve
 * @return serviceConfiguration object
 */
export const getFactoryTalkServiceConfiguration = (state, name) =>
  state.factoryTalkServiceConfigurations?.data[name] || {};

// Reducers for the factory talk source tables
export const factoryTalkSourceTablesReducer = (
  state = { data: {}, fetchingData: false },
  action
) => {
  switch (action.type) {
    case types.GET_FACTORYTALK_SOURCE_TABLES_REQUEST:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
      };
    case types.GET_FACTORYTALK_SOURCE_TABLES_FAILURE:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case types.GET_FACTORYTALK_SOURCE_TABLES_SUCCESS:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: action.payload,
      };

    default:
      return state;
  }
};

// Selector to return factory talk source tables
export const getFactoryTalkSourceTables = state => state.factoryTalkSourceTables?.data || {};
export const isFactoryTalkSourceTablesFetching = state =>
  state.factoryTalkSourceTables?.fetchingData;
export const getFactoryTalkSourceTablesError = state =>
  state.factoryTalkSourceTables?.fetchDataError;

// Reducers for the source tables tags
export const factoryTalkSourceTablesTagsReducer = (state = defaultFetchState, action) => {
  switch (action.type) {
    case types.GET_FACTORYTALK_SOURCE_TABLES_TAGS_REQUEST:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
      };
    case types.GET_FACTORYTALK_SOURCE_TABLES_TAGS_FAILURE:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case types.GET_FACTORYTALK_SOURCE_TABLES_TAGS_SUCCESS:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: action.payload,
      };

    default:
      return state;
  }
};

// Selectors for source tables tags
export const getFactoryTalkSourceTablesTags = state =>
  Object.values(state.factoryTalkSourceTablesTags?.data || {}) || [];
export const isFactoryTalkSourceTablesTagsFetching = state =>
  state.factoryTalkSourceTablesTags?.fetchingData;
export const getFactoryTalkSourceTablesTagsError = state =>
  state.factoryTalkSourceTablesTags?.fetchDataError;

// Reducers for the monitor db
export const monitorDBSchemasReducer = (state = { data: [], fetchingData: false }, action) => {
  switch (action.type) {
    case types.GET_MONITOR_DB_SCHEMAS_REQUEST:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
      };
    case types.GET_MONITOR_DB_SCHEMAS_FAILURE:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case types.GET_MONITOR_DB_SCHEMAS_SUCCESS:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: action.payload,
      };

    default:
      return state;
  }
};

// Selectors to return monitor DB Schemas
export const getMonitorDBSchemas = state => state.monitorDBSchemas?.data || [];
export const isMonitorDBSchemasFetching = state => state.monitorDBSchemas?.fetchingData;
export const getMonitorDBSchemasError = state => state.monitorDBSchemas?.fetchDataError;

// Reducers for validating database connection
export const validateFactoryTalkDatabaseConnectionReducer = (state = defaultFetchState, action) => {
  switch (action.type) {
    case types.GET_FACTORYTALK_VALIDATE_DATABASE_CONNECTION_REQUEST:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
      };
    case types.GET_FACTORYTALK_VALIDATE_DATABASE_CONNECTION_FAILURE:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case types.GET_FACTORYTALK_VALIDATE_DATABASE_CONNECTION_SUCCESS:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: action.payload,
      };

    default:
      return state;
  }
};

// Selectors to return database connection validation
export const isFactoryTalkDatabaseConnectionValid = state =>
  state.validateFactoryTalkDatabaseConnection?.data || [];
export const isFactoryTalkDatabaseConnectionValidationFetching = state =>
  state.validateFactoryTalkDatabaseConnection?.fetchingData;
export const getFactoryTalkDatabaseConnectionValidationError = state =>
  state.validateFactoryTalkDatabaseConnection?.fetchDataError;

// Reducer for fetching a service config file
export const serviceConfigFileReducer = (state = defaultFetchState, action) => {
  switch (action.type) {
    case types.GET_SERVICE_CONFIG_FILE_REQUEST:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
      };
    case types.GET_SERVICE_CONFIG_FILE_FAILURE:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case types.GET_SERVICE_CONFIG_FILE_SUCCESS:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: action.payload,
      };

    default:
      return state;
  }
};

// Selectors for fetching a service config file
export const getServiceConfigFile = state => state.serviceConfigFile?.data || '';
export const isServiceConfigFileFetching = state => state.serviceConfigFile?.fetchingData;
export const getServiceConfigFileError = state => state.serviceConfigFile?.fetchDataError;

// Reducer for fetching service config files
export const serviceConfigFileNamesReducer = (state = defaultFetchState, action) => {
  switch (action.type) {
    case types.GET_SERVICE_CONFIG_FILE_NAMES_REQUEST:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
      };
    case types.GET_SERVICE_CONFIG_FILE_NAMES_FAILURE:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case types.GET_SERVICE_CONFIG_FILE_NAMES_SUCCESS:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: action.payload,
      };

    default:
      return state;
  }
};

// Selectors for fetching a service config files
export const getServiceConfigFileNames = state => state.serviceConfigFileNames?.data || [];
export const isServiceConfigFileNamesFetching = state => state.serviceConfigFileNames?.fetchingData;
export const getServiceConfigFileNamesError = state => state.serviceConfigFileNames?.fetchDataError;

// Reducer for uploading service config file(s)
export const uploadServiceConfigFilesReducer = (state = defaultFetchState, action) => {
  switch (action.type) {
    case types.UPLOAD_SERVICE_CONFIG_FILES_REQUEST:
      return {
        ...state,
        uploadingData: true,
        fetchUploadError: null,
      };
    case types.UPLOAD_SERVICE_CONFIG_FILES_FAILURE:
      return {
        ...state,
        uploadingData: false,
        fetchUploadError: action.payload,
      };
    case types.UPLOAD_SERVICE_CONFIG_FILES_SUCCESS:
      return {
        ...state,
        uploadingData: false,
        fetchUploadError: null,
        // data: action.payload,
      };

    default:
      return state;
  }
};

// Selectors for uploading service config file(s)
export const isServiceConfigFilesUploading = state => state.uploadServiceConfigFiles?.uploadingData;
export const getServiceConfigFilesUploadError = state =>
  state.uploadServiceConfigFiles?.fetchUploadError;

// Reducer for deleting service config file(s)
export const deleteServiceConfigFilesReducer = (state = defaultFetchState, action) => {
  switch (action.type) {
    case types.DELETE_SERVICE_CONFIG_FILES_REQUEST:
      return {
        ...state,
        deletingData: true,
        fetchDeleteError: null,
      };
    case types.DELETE_SERVICE_CONFIG_FILES_FAILURE:
      return {
        ...state,
        deletingData: false,
        fetchDeleteError: action.payload,
      };
    case types.DELETE_SERVICE_CONFIG_FILES_SUCCESS:
      return {
        ...state,
        deletingData: false,
        fetchDeleteError: null,
        // data: action.payload,
      };

    default:
      return state;
  }
};

// Selectors for deleting service config file(s)
export const isServiceConfigFilesDeleting = state => state.deleteServiceConfigFiles?.deletingData;
export const getServiceConfigFilesDeleteError = state =>
  state.deleteServiceConfigFiles?.fetchDeleteError;
